import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { on, removeListener } from '../../helper/electron';
import {
  completeImport,
  setScanComplete,
  startImport,
  updateImportProgress,
  updateScanningProgress,
} from '../../store/slices/global-import-progress.slice';
import { AppDispatch } from '../../store/store';

/**
 * ImportProgressConnector connects Electron IPC events to Redux global import progress state.
 * This component should be mounted once in the app to listen for import events.
 */
const ImportProgressConnector: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // Listen for folder import start events
    const handleFolderImportStart = (data: any) => {
      console.log('🚀 Import started for folder:', data);

      if (data.folder && data.folder.id && data.folder.path) {
        dispatch(
          startImport({
            folderId: data.folder.id,
            folderPath: data.folder.path,
          }),
        );
      }
    };

    // Listen for scan progress events
    const handleScanProgress = (data: any) => {
      console.log('📊 Scan progress:', data);

      if (data.folder && data.folder.id) {
        dispatch(
          setScanComplete({
            folderId: data.folder.id,
            filesFound: data.filesFound || 0,
            errorsCount: data.errorsCount || 0,
            errors: data.errors || [],
          }),
        );
      }

      // Handle scanning progress updates
      if (data.phase === 'scanning' || data.directoriesScanned !== undefined) {
        dispatch(
          updateScanningProgress({
            directoriesScanned: data.directoriesScanned,
            totalDirectories: data.totalDirectories,
            filesFound: data.filesFound,
            currentDirectory: data.currentDirectory,
          }),
        );
      }
    };

    // Listen for import progress events
    const handleImportProgress = (data: any) => {
      console.log('⏳ Import progress:', data);

      if (data.folder && data.folder.id) {
        dispatch(
          updateImportProgress({
            folderId: data.folder.id,
            totalFiles: data.total,
            processedFiles: data.numOfProcessed,
            phase: 'processing',
          }),
        );
      }
    };

    // Listen for import completion events
    const handleImportComplete = (data: any) => {
      console.log('✅ Import complete:', data);

      if (data.folderId) {
        dispatch(
          completeImport({
            folderId: data.folderId,
          }),
        );
      }
    };

    // Listen for folder removal events (import cancelled/failed)
    const handleFolderRemoved = (data: any) => {
      console.log('🗑️ Folder removed/cancelled:', data);

      // We could add logic here to handle folder removal
      // For now, we'll let the import complete naturally
    };

    // Set up event listeners
    on('my-music/import-start', handleFolderImportStart);
    on('my-music/scan-progress', handleScanProgress);
    on('my-music/add-folder', handleImportProgress); // This is the main progress event
    on('my-music/scan-complete', handleImportComplete);
    on('my-music/folder-auto-removed', handleFolderRemoved);

    // Also listen for VirtualDJ and Serato import events
    on('virtualdj/import-progress', handleImportProgress);
    on('virtualdj/import-complete', handleImportComplete);
    on('serato/import-progress', handleImportProgress);
    on('serato/import-complete', handleImportComplete);

    // Cleanup function
    return () => {
      removeListener('my-music/import-start');
      removeListener('my-music/scan-progress');
      removeListener('my-music/add-folder');
      removeListener('my-music/scan-complete');
      removeListener('my-music/folder-auto-removed');
      removeListener('virtualdj/import-progress');
      removeListener('virtualdj/import-complete');
      removeListener('serato/import-progress');
      removeListener('serato/import-complete');
    };
  }, [dispatch]);

  // This component doesn't render anything
  return null;
};

export default ImportProgressConnector;
